import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import _isEqual from 'lodash/isEqual';
import _debounce from 'lodash/debounce';

import {
  KoButton,
  KoDrawer,
  KoToast,
  KoTitle,
  KoLoader,
  KoWhiteboard
} from 'packages';
import KoIcon from '@kwapio/icons';

import styles from './document.module.scss';
import { FilesAPI } from './files.service';
import Driver from 'packages/drivers';
import Comments from './comments';
import KoFileRichTextEditor from 'packages/richText/fileRichText';
import FileSharePanel from './fileSharePanel';

import { downloadFile, exportSlateToDocx } from './downloaddocx';
import { fixSlateDocument, getDrivePermission } from 'shared/utils';
import { getCookie } from 'shared/utils/cookies';

export default function DocumentCreate({ showComments }) {
  const { documentId, folderId } = useParams();
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);
  const [isNewFileLoader, setIsnewFileLoader] = useState(false);
  const [fileDetails, setFileDetails] = useState({
    type: documentId === 'new' ? 'docx' : documentId
  });
  const [jsonData, setJsonData] = useState();
  const [attachments, setAttachments] = useState([]);
  const [totalComments, setCommentsTotal] = useState(0);
  const [savingTextStatus, setSavingTextStatus] = useState('');
  const [openComments, setOpenComments] = useState(false);
  const [openSharing, setOpenSharing] = useState(false);
  const [isOpenCommentsModalNew, setCreateCommentNew] = useState(false);

  const isNew = documentId === 'new' || documentId === 'whiteboard';
  const hasJson = !!jsonData;

  const permission = getDrivePermission(
    fileDetails?.sharedWith,
    getCookie('userId'),
    fileDetails?.createdBy
  );

  const isEditable = permission?.canEdit || isNew;
  const canComment = permission?.canComment;
  const hasAccess =
    permission?.canEdit ||
    permission?.canComment ||
    permission?.canView ||
    isNew;

  useEffect(() => {
    if (documentId && documentId !== 'new' && documentId !== 'whiteboard') {
      fetchFile(documentId);
      fetchComments(documentId);
    }
  }, [documentId]);

  const fetchComments = async(id) => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await FilesAPI.getComments(id);
      setCommentsTotal(res?.totalCount || 0);
    } finally {
      setLoading(false);
    }
  };

  const fetchFileContent = async(url) => {
    if (!url) return;
    setLoading(true);
    try {
      const res = await fetch(url);
      const json = await res.json();
      setJsonData(fixSlateDocument(json?.content) || []);
    } finally {
      setLoading(false);
    }
  };

  const fetchFile = async(id, redirect = false) => {
    if (!id || id === 'new' || id === 'whiteboard') return;
    setLoading(true);
    try {
      const { file } = await FilesAPI.getFileById(id);
      setFileDetails(file);
      setAttachments(file?.files || []);
      if (file?.url) fetchFileContent(file.url);

      if (redirect) {
        setTimeout(() => {
          navigate(`/open/document/${folderId}/${id}`);
          setIsnewFileLoader(false);
        }, 500);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFile = (value) => {
    const content = value || [
      { type: 'paragraph', children: [{ text: '' }] }
    ];

    const values = {
      type: fileDetails?.type || 'docx',
      name: fileDetails?.name || 'Untitled Document',
      content,
      files: attachments,
      groups: fileDetails?.groups || [],
      users: fileDetails?.users || [],
      ...(folderId ? { folderId } : {})
    };

    setSavingTextStatus('Saving...');
    setLoading(true);

    FilesAPI.createOrUpdateFile(values, documentId, folderId ? { folderId } : {})
      .then(({ documentId: id }) => {
        setSavingTextStatus('Saved');
        fetchFile(id, isNew);
      })
      .catch(() => KoToast.error('Save failed'))
      .finally(() => setLoading(false));
  };

  const debounceSave = useMemo(
    () =>
      _debounce((value) => {
        if (isEditable) handleSaveFile(value);
      }, 2000),
    [isEditable]
  );

  const renameDocument = (name) => {
    if (isNew) {
      setIsnewFileLoader(true);
      setSavingTextStatus('Saving...');

      const values = {
        type: 'docx',
        name,
        groups: fileDetails?.groups || [],
        users: fileDetails?.users || [],
        content: [{ type: 'paragraph', children: [{ text: '' }] }],
        files: attachments || [],
        ...(folderId ? { folderId } : {})
      };

      FilesAPI.createOrUpdateFile(values, documentId, folderId ? { folderId } : {})
        .then((data) => {
          setSavingTextStatus('Saved to drive');
          fetchFile(data?.documentId, true);
          setTimeout(() => setSavingTextStatus(''), 2000);
        })
        .catch((err) => {
          setIsnewFileLoader(false);
          KoToast.error(err?.error || 'Save failed');
        });
    } else {
      FilesAPI.renameDocument({ name }, documentId)
        .then(() => KoToast.success('Document renamed successfully!'))
        .catch(() => KoToast.error('Rename failed'));
    }
  };

  const editorKey = useMemo(() => {
    if (isNew) return 'new-editor';
    return `${fileDetails?.documentId}-${fileDetails?.updatedAt || fileDetails?.url}`;
  }, [fileDetails?.documentId, fileDetails?.updatedAt, fileDetails?.url, isNew]);

  const editorProps = {
    attachments,
    isLoading,
    totalComments,
    fileDetails,
    savingTextStatus,
    canEdit: isEditable,
    canComment,
    onUpdateAttachment: (f) => setAttachments((prev) => [...prev, ...f]),
    onUpdateName: renameDocument,
    onChangeEditorValues: debounceSave,
    fileName: fileDetails?.name,
    folderId,
    onDownload: () =>
      fileDetails?.type === 'docx'
        ? exportSlateToDocx(
          fileDetails?.url,
            `${fileDetails?.name}.docx`,
            setLoading,
            setSavingTextStatus('Downloading...')
        )
        : downloadFile(
          fileDetails?.url,
          fileDetails?.name,
          setLoading,
          setSavingTextStatus('Downloading...')
        ),
    onComments: () => setOpenComments(true),
    onShare: () => setOpenSharing(true),
    initialValues: hasJson
      ? jsonData
      : fileDetails?.type === 'docx'
        ? [{ type: 'paragraph', children: [{ text: '' }] }]
        : [{
          bgColor: '#ffffff',
          elements: [],
          files: {}
        }]
  };



  const isDocx = fileDetails?.type === "docx";
  const isComment = permission?.canComment;

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value) => {
      const permission = getDrivePermission(
        fileDetails?.sharedWith,
        getCookie('userId'),
        fileDetails?.createdBy
      );
      if (permission?.canEdit || isNew) {
        handleSaveFile(value, true);
      }
    };

    return _debounce(loadOptions, 2000);
  }, [fileDetails, handleSaveFile]); // ✅ Fix: add `fileDetails` and `handleSaveFile`

  
  // Editor change handler
  const onChangeEditorValues = (value) => {
    debounceFetcher(value);
  };

  const renderByType = () => {
    switch (fileDetails?.type) {
    case 'docx':
      return (
        isDocx && hasJson ? (
          !isEditable ? (
            <KoFileRichTextEditor
              onUpdateAttachment={(f) => setAttachments((prev) => [...prev, ...f])}
              attachments={attachments}
              initialValues={jsonData}
              isLoading={isLoading}
              fileDetails={fileDetails}
              totalComments={totalComments}
              savingTextStatus={savingTextStatus}
              canEdit={isEditable}
              canComment={isComment}
              onUpdateName={renameDocument}
              onChangeEditorValues={onChangeEditorValues}
              fileName={fileDetails?.name}
              folderId={folderId}
              onDownload={() => {
                fileDetails?.type === 'docx'
                  ? exportSlateToDocx(
                    fileDetails?.url,
                    fileDetails?.name + '.docx',
                    setLoading,
                    setSavingTextStatus('Downloading...')
                  )
                  : downloadFile(
                    fileDetails?.url,
                    fileDetails?.name,
                    setLoading,
                    setSavingTextStatus('Downloading...')
                  );
              }}
              onComments={() => setOpenComments(true)}
              onShare={() => setOpenSharing(true)}
            />
          ) : (
            <KoFileRichTextEditor
              onUpdateAttachment={(f) => setAttachments((prev) => [...prev, ...f])}
              attachments={attachments}
              initialValues={jsonData}
              isLoading={isLoading}
              totalComments={totalComments}
              fileDetails={fileDetails}
              savingTextStatus={savingTextStatus}
              canEdit={isEditable}
              canComment={isComment}
              onUpdateName={renameDocument}
              onChangeEditorValues={onChangeEditorValues}
              fileName={fileDetails?.name}
              folderId={folderId}
              onDownload={() => {
                fileDetails?.type === 'docx'
                  ? exportSlateToDocx(
                    fileDetails?.url,
                    fileDetails?.name + '.docx',
                    setLoading,
                    setSavingTextStatus('Downloading...')
                  )
                  : downloadFile(
                    fileDetails?.url,
                    fileDetails?.name,
                    setLoading,
                    setSavingTextStatus('Downloading...')
                  );
              }}
              onComments={() => setOpenComments(true)}
              onShare={() => setOpenSharing(true)}
            />
          )
        ) : (isNew && !hasJson) && (
          <KoFileRichTextEditor
            onUpdateAttachment={(f) => setAttachments((prev) => [...prev, ...f])}
            attachments={attachments}
            totalComments={totalComments}
            initialValues={[
              {
                type: 'paragraph',
                children: [{ text: '' }]
              }
            ]}
            isLoading={isLoading}
            fileDetails={fileDetails}
            savingTextStatus={savingTextStatus}
            canEdit={isEditable}
            canComment={isComment}
            onUpdateName={renameDocument}
            onChangeEditorValues={onChangeEditorValues}
            fileName={fileDetails?.name}
            folderId={folderId}
            onDownload={() => {
              fileDetails?.type === 'docx'
                ? exportSlateToDocx(
                  fileDetails?.url,
                  fileDetails?.name + '.docx',
                  setLoading,
                  setSavingTextStatus('Downloading...')
                )
                : downloadFile(
                  fileDetails?.url,
                  fileDetails?.name,
                  setLoading,
                  setSavingTextStatus('Downloading...')
                );
            }}
            onComments={() => setOpenComments(true)}
            onShare={() => setOpenSharing(true)}
          />
        )

      );

    case 'whiteboard':
      return (
        <KoWhiteboard
          key={editorKey}
          editorProps={editorProps}
        />
      );

    default:
      return (
        !isNew && (
          <div className={styles.mediaPreview}>
            <Driver
              fileType={fileDetails?.type}
              filePath={fileDetails?.url}
            />
          </div>
        )
      );
    }
  };

  if (!hasAccess && hasJson) {
    return (
      <div className={styles.wrapperEmpty}>
        <KoTitle level={1} text="You do not have permission to view this file" />
        <KoButton
          type="primary"
          label="Request Access"
          onClick={() => {
            setIsnewFileLoader(true);
            setTimeout(() => setIsnewFileLoader(false), 2000);
          }}
        />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {isNewFileLoader && <KoLoader />}
      {renderByType()}

      <KoDrawer
        placement="right"
        open={openComments}
        width={750}
        footer={false}
        closable={false}
        onClose={() => setOpenComments(false)}
        title={
          <div className={styles.headerTitle}>
            <KoTitle level={4} text="Comments" />
            <KoButton
              type="primary"
              label="Add a Comment"
              icon={
                <KoIcon
                  color="#FFF"
                  width={18}
                  height={18}
                  name="PlusOutlined"
                />
              }
              onClick={() => setCreateCommentNew(true)}
            />
          </div>
        }
      >
        {showComments &&
          fileDetails?.documentId &&
          (permission?.canEdit || permission?.canComment) && (
          <Comments
            documentId={fileDetails.documentId}
            updateCount={() =>
              fetchComments(fileDetails.documentId)
            }
            isOpenCommentsModal={isOpenCommentsModalNew}
            setCreateComment={setCreateCommentNew}
            emptyRowHeight={styles.wrapperOverflow}
          />
        )}
      </KoDrawer>

      {openSharing && (
        <FileSharePanel
          title={fileDetails?.name}
          initialShared={fileDetails?.sharedWith}
          onClose={() => setOpenSharing(false)}
          onSave={(sharedWith) => {
            if (!_isEqual(fileDetails?.sharedWith, sharedWith)) {
              FilesAPI.fileShare({
                documentId: [fileDetails.documentId],
                sharedWith,
                name: 'file'
              }).then(() => fetchFile(fileDetails.documentId));
            }
          }}
        />
      )}
    </div>
  );
}
